import React, { useEffect } from 'react'
import classNames from 'classnames'
import { Container } from 'components/layout/Container'
import { TopNav } from 'transfix-ui/components/TopNav'
import { Box, createStyles, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { TableEmptyState } from 'apps/fleet-planner/components/atoms/TableEmptyState'
import { BidsCardRow } from 'apps/fleet-planner/components/bids/BidsCardRow'
import { BidsTableRow } from 'apps/fleet-planner/components/bids/BidsTableRow'
import { BidsTableRowLoadingState } from 'apps/fleet-planner/components/bids/BidsTableRowLoadingState'
import { useTrackConversionContext } from 'apps/fleet-planner/context/track-conversion-source/TrackConversionContext'
import { useBidsApi } from 'apps/fleet-planner/hooks/useBidsApi'
import { MarketplaceLoadDetailsModalContainer } from '../marketplace/MarketplaceLoadDetailsContainer'

const useBidsPageStyles = makeStyles(() =>
  createStyles({
    counterTab: {
      backgroundColor: brandColors.skyBlue2,
      color: brandColors.skyBlue7,
    },
    counterTabEmpty: {
      backgroundColor: brandColors.coolGray4,
      color: brandColors.coolGray6,
    },
  })
)

export const BidsPage = () => {
  const { acceptedBids, declinedBids, getCarrierBids, isLoading, pendingBids } = useBidsApi()
  const { isMobile } = useBreakpointContext()
  const { setTrackConversion } = useTrackConversionContext()
  const classes = useBidsPageStyles()
  const tabClass = acceptedBids.length > 0 ? classes.counterTab : classes.counterTabEmpty

  // Fetch carrier bids on page load
  useEffect(() => {
    getCarrierBids()
  }, [])

  const closeMarketplaceLoadDetailsModalHandler = () => {
    getCarrierBids()
  }

  const attributionClickHandler = () => {
    setTrackConversion('web', 'my_bids_page')
  }

  return (
    <Box>
      <TopNav showBackButton title='My Bids' showMenu={!isMobile} />
      <Container hidePadding>
        <Box data-test='bids-container' pt={2} px={2}>
          <Box pt={2}>
            <Typography variant='h3'>Bid History</Typography>
          </Box>
          <Box pt={isMobile ? 3 : 4}>
            <Typography component='span' variant='subtitle1'>
              Accepted Bids
              {acceptedBids?.length > 0 && (
                <span className={classNames('tab-counter', tabClass)}>{acceptedBids?.length}</span>
              )}
            </Typography>
            {/* Loading state */}
            {isLoading && <BidsTableRowLoadingState />}
            {/* Empty state */}
            {!isLoading && acceptedBids?.length === 0 && (
              <Box pt={2}>
                <TableEmptyState
                  messageOne='No Accepted Bids'
                  messageTwo='Bids that have been accepted live here'
                />
              </Box>
            )}
            {/* Carrier Accepted Bids */}
            {!isLoading &&
              acceptedBids?.length > 0 &&
              acceptedBids.map(acceptedBid => {
                const { id } = acceptedBid || {}
                return isMobile ? (
                  <BidsCardRow onClick={attributionClickHandler} key={id} bid={acceptedBid} />
                ) : (
                  <BidsTableRow
                    onClick={attributionClickHandler}
                    dataTest={`accepted-bids-row-${id}`}
                    key={id}
                    bid={acceptedBid}
                  />
                )
              })}
          </Box>
          <Box pt={4}>
            <Typography variant='subtitle1'>Pending Bids</Typography>
            {/* Loading state */}
            {isLoading && <BidsTableRowLoadingState />}
            {/* Empty state */}
            {!isLoading && pendingBids?.length === 0 && (
              <Box pt={2}>
                <TableEmptyState
                  messageOne='No Pending Bids'
                  messageTwo={`Bids you've placed on loads live here`}
                />
              </Box>
            )}
            {/* Carrier Pending Bids */}
            {!isLoading &&
              pendingBids?.length > 0 &&
              pendingBids.map(pendingBid => {
                const { id } = pendingBid || {}
                return isMobile ? (
                  <BidsCardRow onClick={attributionClickHandler} key={id} bid={pendingBid} />
                ) : (
                  <BidsTableRow
                    onClick={attributionClickHandler}
                    dataTest={`pending-bids-row-${id}`}
                    key={id}
                    bid={pendingBid}
                  />
                )
              })}
          </Box>
          <Box pt={4}>
            <Typography variant='subtitle1'>Declined Bids</Typography>
            {/* Loading state */}
            {isLoading && <BidsTableRowLoadingState />}
            {/* Empty state */}
            {!isLoading && declinedBids?.length === 0 && (
              <Box pt={2}>
                <TableEmptyState
                  messageOne='No declined bids'
                  messageTwo='Declined bids from the past 7 days live here'
                />
              </Box>
            )}
            {/* Carrier Declined Bids */}
            {!isLoading &&
              declinedBids?.length > 0 &&
              declinedBids.map(declinedBid => {
                const { id } = declinedBid || {}
                return isMobile ? (
                  <BidsCardRow onClick={attributionClickHandler} key={id} bid={declinedBid} />
                ) : (
                  <BidsTableRow
                    onClick={attributionClickHandler}
                    dataTest={`declined-bids-row-${id}`}
                    key={id}
                    bid={declinedBid}
                  />
                )
              })}
          </Box>
        </Box>
      </Container>
      <MarketplaceLoadDetailsModalContainer
        onModalClose={closeMarketplaceLoadDetailsModalHandler}
        route='bids'
      />
    </Box>
  )
}
